import React, { useEffect } from 'react';
import type { RefObject, Dispatch, SetStateAction } from 'react';

import { validate as uuidValidate } from 'uuid';

import Konva from 'konva';
import type { KonvaEventObject, Node } from 'konva/lib/Node';
import { Layer, Rect, Stage, Transformer } from 'react-konva';

import { FlexBox } from '@eltoro-ui/components';

// models
import { SceneManager } from 'Components/CanvasEditor/models/SceneManager';

// canvas elements
import BackgroundImage from 'Components/CanvasEditor/components/elements/BackgroundImage';
import ButtonElement from 'Components/CanvasEditor/components/elements/Button';
import TextElement from 'Components/CanvasEditor/components/elements/Text';
import ImageElement from 'Components/CanvasEditor/components/elements/Image';
import ShapeElement from 'Components/CanvasEditor/components/elements/Shape';

import type {
  CanvasBackground,
  CanvasElement,
  ICanvasDimensions,
  ButtonElement as ButtonElementType,
} from 'Components/CanvasEditor/types';

type TGuide = { lineGuide: number; offset: number; orientation: 'V' | 'H'; snap: string };

type TLineGuideStops = {
  vertical: number[];
  horizontal: number[];
};

type ObjectSnappingEdge = { guide: number; offset: number; snap: string };

type TObjectSnappingEdges = {
  vertical: ObjectSnappingEdge[];
  horizontal: ObjectSnappingEdge[];
};

const GUIDELINE_OFFSET = 5;

interface EditorProps {
  stageRef: RefObject<Konva.Stage>;
  layerRef: RefObject<Konva.Layer>;
  transformerRef: RefObject<Konva.Transformer>;
  sceneManager: [SceneManager | null, Dispatch<SetStateAction<SceneManager | null>>];
  setActiveCropper: Dispatch<SetStateAction<boolean>>;
  canvasDimensions: ICanvasDimensions;
  background: CanvasBackground;
  selectedId: string | null;
  selectedElement: CanvasElement | null;
  elements: CanvasElement[];
  onElementChange: (props: Partial<CanvasElement>, id: string, saveToHistory?: boolean) => void;
  onSelectElement: (id: string | null) => void;
}

export default function Editor({
  stageRef,
  layerRef,
  transformerRef,
  sceneManager,
  setActiveCropper,
  canvasDimensions: { canvasWidth, canvasHeight },
  background,
  selectedId,
  selectedElement,
  elements,
  onElementChange,
  onSelectElement,
}: EditorProps) {
  useEffect(() => {
    if (selectedId && transformerRef.current) {
      // Find the selected node
      const selectedNode = layerRef?.current?.findOne(`#${selectedId}`);
      if (selectedNode) {
        transformerRef.current.nodes([selectedNode]);
        transformerRef?.current?.getLayer()?.batchDraw();
      }
    } else if (transformerRef.current) {
      transformerRef.current.nodes([]);
      transformerRef?.current?.getLayer()?.batchDraw();
    }
  }, [selectedId, elements]);

  const handleStageClick = (e: KonvaEventObject<Event>) => {
    if (e.target === e.currentTarget) {
      onSelectElement(null);
      return;
    }

    const clickedOnTransformer = e.target.getParent()?.className === 'Transformer';
    if (clickedOnTransformer) return;

    const id = e.target.id();
    const isValidUUID = uuidValidate(id);
    if (isValidUUID) onSelectElement(id);
    else {
      const parent = e.target.getParent();
      onSelectElement(parent.id());
    }
  };

  const onTransformEnd = (e: KonvaEventObject<Event>) => {
    const node = e.currentTarget;
    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    const anchor = transformerRef?.current?.getActiveAnchor();

    // Reset scale to 1 and adjust dimensions
    let newProps: Partial<CanvasElement> = {
      x: node.x(),
      y: node.y(),
      width: node.width() * scaleX,
      height: node.height() * scaleY,
      scaleX: 1,
      scaleY: 1,
    };

    // Reset scale to 1
    node.scaleX(1);
    node.scaleY(1);

    if (node instanceof Konva.Line && anchor?.includes('middle')) {
      const [x1, y1, x2, y2] = node.points();
      const newX2 = x2 * scaleX;
      const newY2 = y2 * scaleY;

      newProps = { ...newProps, points: [x1, y1, newX2, newY2] };
    }

    if (node instanceof Konva.Ellipse) {
      const radiusX = Math.floor((node.width() * scaleX) / 2);
      const radiusY = Math.floor((node.height() * scaleY) / 2);

      newProps = { ...newProps, radiusX, radiusY };
    }

    onElementChange({ ...newProps }, node.id(), true);
  };

  const onDragStart = (e: KonvaEventObject<Event>) => {
    const node = e.currentTarget;
    onSelectElement(node.id());
  };

  const onDragEnd = (e: KonvaEventObject<DragEvent>) => {
    const node = e.target;
    onElementChange({ x: node.x(), y: node.y() }, node.id(), true);
  };

  // were can we snap our objects?
  function getLineGuideStops(skipShape: Node): TLineGuideStops {
    const stage = stageRef.current;
    if (!stage) return { vertical: [], horizontal: [] };

    // we can snap to stage borders and the center of the stage
    let vertical = [0, stage.width() / 2, stage.width()];
    let horizontal = [0, stage.height() / 2, stage.height()];

    // and we snap over edges and center of each object on the canvas
    stage.find('.object').forEach(guideItem => {
      if (guideItem === skipShape) {
        return;
      }
      let box = guideItem.getClientRect();
      // and we can snap to all edges of shapes
      vertical.push(...[box.x, box.x + box.width, box.x + box.width / 2]);
      horizontal.push(...[box.y, box.y + box.height, box.y + box.height / 2]);
    });
    return {
      vertical: vertical.flat(),
      horizontal: horizontal.flat(),
    };
  }

  // what points of the object will trigger to snapping?
  // it can be just center of the object
  // but we will enable all edges and center
  function getObjectSnappingEdges(node: Node): TObjectSnappingEdges {
    let box = node.getClientRect();
    let absPos = node.absolutePosition();

    return {
      vertical: [
        {
          guide: Math.round(box.x),
          offset: Math.round(absPos.x - box.x),
          snap: 'start',
        },
        {
          guide: Math.round(box.x + box.width / 2),
          offset: Math.round(absPos.x - box.x - box.width / 2),
          snap: 'center',
        },
        {
          guide: Math.round(box.x + box.width),
          offset: Math.round(absPos.x - box.x - box.width),
          snap: 'end',
        },
      ],
      horizontal: [
        {
          guide: Math.round(box.y),
          offset: Math.round(absPos.y - box.y),
          snap: 'start',
        },
        {
          guide: Math.round(box.y + box.height / 2),
          offset: Math.round(absPos.y - box.y - box.height / 2),
          snap: 'center',
        },
        {
          guide: Math.round(box.y + box.height),
          offset: Math.round(absPos.y - box.y - box.height),
          snap: 'end',
        },
      ],
    };
  }

  // find all snapping possibilities
  function getGuides(lineGuideStops: TLineGuideStops, itemBounds: TObjectSnappingEdges) {
    let resultV: { lineGuide: number; diff: number; snap: string; offset: number }[] = [];
    let resultH: { lineGuide: number; diff: number; snap: string; offset: number }[] = [];

    lineGuideStops.vertical.forEach(lineGuide => {
      itemBounds.vertical.forEach(itemBound => {
        let diff = Math.abs(lineGuide - itemBound.guide);
        // if the distance between guild line and object snap point is close we can consider this for snapping
        if (diff < GUIDELINE_OFFSET) {
          resultV.push({
            lineGuide,
            diff,
            snap: itemBound.snap,
            offset: itemBound.offset,
          });
        }
      });
    });

    lineGuideStops.horizontal.forEach(lineGuide => {
      itemBounds.horizontal.forEach(itemBound => {
        let diff = Math.abs(lineGuide - itemBound.guide);
        if (diff < GUIDELINE_OFFSET) {
          resultH.push({
            lineGuide,
            diff,
            snap: itemBound.snap,
            offset: itemBound.offset,
          });
        }
      });
    });

    let guides: TGuide[] = [];

    // find closest snap
    let minV = resultV.sort((a, b) => a.diff - b.diff)[0];
    let minH = resultH.sort((a, b) => a.diff - b.diff)[0];

    if (minV) {
      guides.push({
        lineGuide: minV.lineGuide,
        offset: minV.offset,
        orientation: 'V',
        snap: minV.snap,
      });
    }
    if (minH) {
      guides.push({
        lineGuide: minH.lineGuide,
        offset: minH.offset,
        orientation: 'H',
        snap: minH.snap,
      });
    }

    return guides;
  }

  function drawGuides(guides: TGuide[]) {
    const layer = layerRef.current;
    if (!layer) return;

    guides.forEach(lg => {
      if (lg.orientation === 'H') {
        const line = new Konva.Line({
          points: [-6000, 0, 6000, 0],
          stroke: 'rgb(0, 161, 255)',
          strokeWidth: 1,
          name: 'guid-line',
          dash: [4, 6],
        });
        layer.add(line);
        line.absolutePosition({
          x: 0,
          y: lg.lineGuide,
        });
      } else if (lg.orientation === 'V') {
        let line = new Konva.Line({
          points: [0, -6000, 0, 6000],
          stroke: 'rgb(0, 161, 255)',
          strokeWidth: 1,
          name: 'guid-line',
          dash: [4, 6],
        });
        layer.add(line);
        line.absolutePosition({
          x: lg.lineGuide,
          y: 0,
        });
      }
    });
  }

  const onLayerDragMove = (e: KonvaEventObject<DragEvent>) => {
    const layer = layerRef.current;
    if (!layer) return;
    // clear all previous lines on the screen
    layer.find('.guid-line').forEach(l => l.destroy());

    // find possible snapping lines
    let lineGuideStops = getLineGuideStops(e.target);

    // find snapping points of current object
    let itemBounds = getObjectSnappingEdges(e.target);

    // now find where can we snap current object
    let guides = getGuides(lineGuideStops, itemBounds);

    // do nothing of no snapping
    if (!guides.length) {
      return;
    }

    drawGuides(guides);

    let absPos = e.target.absolutePosition();
    // now force object position
    guides.forEach(lg => {
      if (lg.orientation === 'V') {
        absPos.x = lg.lineGuide + lg.offset;
      } else if (lg.orientation === 'H') {
        absPos.y = lg.lineGuide + lg.offset;
      }
    });
    e.target.absolutePosition(absPos);
  };

  const onLayerDragEnd = () => {
    const layer = layerRef.current;
    if (!layer) return;
    // clear all previous lines on the screen
    layer.find('.guid-line').forEach(l => l.destroy());
  };

  return (
    <FlexBox justifyContent="center" alignItems="center" UNSAFE_className="canvas-editor">
      <FlexBox flexDirection="column" gap="0.5rem">
        <span className="canvas-banner-dimensions">
          {canvasWidth}x{canvasHeight}
        </span>
        <div className="canvas-wrapper">
          <Stage
            ref={stageRef}
            width={canvasWidth}
            height={canvasHeight}
            onClick={handleStageClick}
          >
            <Layer ref={layerRef} onDragMove={onLayerDragMove} onDragEnd={onLayerDragEnd}>
              {background.type === 'image' && (
                <BackgroundImage
                  background_url={background.value}
                  canvasDimensions={{ canvasWidth, canvasHeight }}
                />
              )}
              {background.type === 'color' && (
                <Rect
                  id="background-color"
                  x={0}
                  y={0}
                  width={canvasWidth}
                  height={canvasHeight}
                  fill={background.value}
                />
              )}
              {elements.map(element => {
                if (element.type === 'text') {
                  if ('background' in element && 'cornerRadius' in element)
                    return (
                      <ButtonElement
                        key={element.id}
                        element={element as ButtonElementType}
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                        onElementChange={onElementChange}
                      />
                    );
                  return (
                    <TextElement
                      key={element.id}
                      element={element}
                      onDragStart={onDragStart}
                      onDragEnd={onDragEnd}
                      onElementChange={onElementChange}
                    />
                  );
                }
                if (element.type === 'image')
                  return (
                    <ImageElement
                      key={element.id}
                      stageRef={stageRef}
                      layerRef={layerRef}
                      sceneManager={sceneManager}
                      setActiveCropper={setActiveCropper}
                      onElementChange={onElementChange}
                      onSelectElement={onSelectElement}
                      element={element}
                      selectedId={selectedId}
                    />
                  );

                if (['rect', 'circle', 'line'].includes(element.type))
                  return (
                    <ShapeElement
                      key={element.id}
                      element={element}
                      onTransformEnd={onTransformEnd}
                      onDragStart={onDragStart}
                      onDragEnd={onDragEnd}
                    />
                  );
                return null;
              })}
              {selectedElement?.type !== 'image' && (
                <Transformer
                  ref={transformerRef}
                  borderStrokeWidth={2}
                  anchorStrokeWidth={2}
                  rotateAnchorOffset={30}
                  rotationSnaps={[0, 90, 180, 270]}
                  onDragMove={evt => (evt.cancelBubble = true)}
                  flipEnabled={selectedElement?.type !== 'text'}
                  boundBoxFunc={(_, newBox) => newBox}
                  enabledAnchors={[
                    'middle-left',
                    'middle-right',
                    ...(selectedElement?.type !== 'text' && selectedElement?.type !== 'line'
                      ? [
                          'top-center',
                          'bottom-center',
                          'top-left',
                          'top-right',
                          'bottom-left',
                          'bottom-right',
                        ]
                      : []),
                  ]}
                />
              )}
            </Layer>
          </Stage>
        </div>
      </FlexBox>
    </FlexBox>
  );
}
